import React from 'react';
import { IMAGE_UPLOAD_ACCEPT_ATTR } from '../../../utils/imageUploadCompatibility';
import DsButton from '../DsButton';
import DsModal from '../DsModal';
import { formatDateTimeJST } from '../../../utils/datetime';
import {
  formatBytes,
  getLogoFillColorValue,
  LOGO_FILL_COLORS,
  logoSupportsFillColor,
  MAX_BATCH_FILES,
  TEXT_PRESETS,
  UPLOAD_STEPS,
  WATERMARK_POSITIONS,
  WATERMARK_PRESETS,
  WatermarkSamplePreview,
} from './EventImageGallery.helpers';
import DsSelect from '../DsSelect';

export default function EventImageGalleryView(props) {
  const {
    activeImage,
    activeImageIndex,
    buildEventImageFileUrl,
    buildEventImagePreviewUrl,
    busy,
    canDownload,
    canUpload,
    clearSpringFolder,
    createFolder,
    creatingFolder,
    customLogoInputRef,
    deleteOpen,
    deleteSelected,
    downloadSelected,
    dragOver,
    dropTargetId,
    eventDateLabel,
    eventName,
    eventUuid,
    fileInputRef,
    folderId,
    folders,
    galleryOpen,
    handleCustomLogo,
    handleGalleryDragOver,
    handleGalleryDrop,
    handleSelectedFiles,
    hasExternalFiles,
    images,
    limits,
    loading,
    moveActiveImage,
    newFolderName,
    openFolder,
    openImagePreview,
    openUploadWizard,
    path,
    pendingFiles,
    previewBusy,
    previewImages,
    processedPreviews,
    resetUploadDraft,
    sampleOrientation,
    sampleTone,
    selectedAllInView,
    selectedCount,
    selectedIds,
    showTitle,
    setActiveImageId,
    setCreatingFolder,
    setDeleteOpen,
    setDragOver,
    setDragPayload,
    setDropTargetId,
    setGalleryOpen,
    setNewFolderName,
    setSampleOrientation,
    setSampleTone,
    setSelectedIds,
    setSettings,
    setUploadOpen,
    setUploadStep,
    setUploadTargetFolderId,
    settings,
    startImageDrag,
    toggleSelected,
    uploadOpen,
    uploadPendingFiles,
    uploadProgress,
    uploadStep,
    uploadStepIndex,
    usage,
  } = props;

  const [activeSettingsTab, setActiveSettingsTab] = React.useState('logo');
  const enabledSettingsTabs = [
    settings.logoEnabled ? { id: 'logo', label: '透かし' } : null,
    settings.textEnabled ? { id: 'text', label: 'テキスト' } : null,
  ].filter(Boolean);
  const visibleSettingsTab = enabledSettingsTabs.some((tab) => tab.id === activeSettingsTab)
    ? activeSettingsTab
    : enabledSettingsTabs[0]?.id;
  const handleStepClick = (stepId, index) => {
    if (busy) return;
    if (stepId === uploadStep) return;
    if (Math.abs(index - uploadStepIndex) !== 1) return;
    if (stepId === 'confirm' && !pendingFiles.length) return;
    setUploadStep(stepId);
  };

  return (
    <div
      className={`event-image-gallery${dragOver ? ' is-drag-over' : ''}`}
      onDragOver={(event) => handleGalleryDragOver(event, 'root')}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        clearSpringFolder();
        setDragOver(false);
        setDropTargetId(null);
      }}
      onDrop={(event) => handleGalleryDrop(event, folderId)}
    >
      <div className="eig-head">
        <div>
          {showTitle ? <h3>イベントアルバム</h3> : null}
          <div className="eig-meta">
            <span>{Number(usage.eventCount || 0)}枚</span>
            <span>{formatBytes(usage.eventBytes)} / {formatBytes(limits.max_event_size || 1024 * 1024 * 1024)}</span>
          </div>
          <div className="eig-breadcrumb" aria-label="イベントアルバムフォルダ">
            <button
              type="button"
              className={dropTargetId === 'breadcrumb-root' ? 'is-drop-target' : ''}
              onClick={() => openFolder(null)}
              onDragOver={(event) => handleGalleryDragOver(event, 'breadcrumb-root')}
              onDrop={(event) => handleGalleryDrop(event, null)}
            >
              すべて
            </button>
            {path.map((item) => (
              <React.Fragment key={item.id}>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <button
                  type="button"
                  className={dropTargetId === `breadcrumb-${item.id}` ? 'is-drop-target' : ''}
                  onClick={() => openFolder(item.id)}
                  onDragOver={(event) => handleGalleryDragOver(event, `breadcrumb-${item.id}`)}
                  onDrop={(event) => handleGalleryDrop(event, item.id)}
                >
                  {item.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="eig-toolbar">
          {selectedCount > 0 ? (
            <>
              <span className="eig-selection">{selectedCount}枚選択中</span>
              <DsButton type="button" variant="secondary" size="sm" onClick={() => setSelectedIds(new Set())}>解除</DsButton>
              {canDownload ? <DsButton type="button" size="sm" onClick={downloadSelected}>選択DL</DsButton> : null}
              {canUpload ? (
                <DsButton type="button" variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
                  <i className="fa-solid fa-trash" aria-hidden="true" /> 削除
                </DsButton>
              ) : null}
            </>
          ) : null}
          {canUpload ? (
            <>
              <DsButton type="button" variant="secondary" size="sm" onClick={() => setCreatingFolder((value) => !value)}>
                <i className="fa-solid fa-folder-plus" aria-hidden="true" /> フォルダ
              </DsButton>
              <DsButton type="button" size="sm" onClick={() => openUploadWizard(folderId)}>
                <i className="fa-solid fa-images" aria-hidden="true" /> 画像を追加
              </DsButton>
            </>
          ) : null}
        </div>
      </div>

      {creatingFolder ? (
        <div className="eig-create">
          <i className="fa-solid fa-folder" aria-hidden="true" />
          <input value={newFolderName} onChange={(event) => setNewFolderName(event.target.value)} placeholder="フォルダ名" />
          <DsButton type="button" size="sm" loading={busy} disabled={!newFolderName.trim()} onClick={createFolder}>作成</DsButton>
          <DsButton type="button" variant="secondary" size="sm" onClick={() => setCreatingFolder(false)}>キャンセル</DsButton>
        </div>
      ) : null}

      {folders.length ? (
        <div className="eig-folder-row">
          {folders.map((folder) => (
            <button
              key={folder.id}
              type="button"
              className={`eig-folder${dropTargetId === `folder-${folder.id}` ? ' is-drop-target' : ''}`}
              onClick={() => openFolder(folder.id)}
              onDragOver={(event) => handleGalleryDragOver(event, `folder-${folder.id}`, folder.id)}
              onDrop={(event) => handleGalleryDrop(event, folder.id)}
            >
              <i className="fa-solid fa-folder" aria-hidden="true" />
              <span>{folder.name}</span>
              <small>{folder.image_count || 0}</small>
            </button>
          ))}
        </div>
      ) : null}

      {loading && !images.length && !folders.length ? <div className="eig-empty">読み込み中です。</div> : null}
      {!loading && !images.length && !folders.length ? (
        <div className="eig-empty">登録されたイベントアルバム画像はありません。</div>
      ) : null}
      {images.length ? (
        <div className="eig-grid">
          {images.map((image) => (
            <div
              key={image.id}
              className={`eig-thumb${selectedIds.has(image.id) ? ' is-selected' : ''}`}
              draggable={canUpload}
              onDragStart={(event) => startImageDrag(event, image)}
              onDragEnd={() => {
                clearSpringFolder();
                setDragPayload(null);
                setDropTargetId(null);
                setDragOver(false);
              }}
            >
              <button
                type="button"
                className="eig-thumb-open"
                onClick={() => openImagePreview(image.id)}
                aria-label={`${image.file_name || 'イベントアルバム画像'}を拡大表示`}
              >
                <img src={buildEventImagePreviewUrl(image.preview_url) || buildEventImageFileUrl(eventUuid, image.id)} alt={image.file_name || 'イベントアルバム画像'} loading="lazy" draggable={false} />
              </button>
              <button
                type="button"
                className="eig-thumb-check"
                aria-label={selectedIds.has(image.id) ? '選択を解除' : '画像を選択'}
                aria-pressed={selectedIds.has(image.id)}
                onClick={() => toggleSelected(image.id)}
              >
                <i className="fa-solid fa-check" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {loading && (images.length || folders.length) ? <div className="eig-loading-more">残りの画像を読み込んでいます。</div> : null}
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_UPLOAD_ACCEPT_ATTR}
        multiple
        hidden
        onChange={async (event) => {
          if (!uploadOpen) setUploadTargetFolderId(folderId ?? null);
          await handleSelectedFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <input ref={customLogoInputRef} type="file" accept={`${IMAGE_UPLOAD_ACCEPT_ATTR},image/svg+xml,.svg`} hidden onChange={handleCustomLogo} />

      <DsModal
        open={deleteOpen}
        onClose={() => {
          if (!busy) setDeleteOpen(false);
        }}
        title="選択した画像を削除しますか？"
        dismissible={!busy}
        footer={(
          <>
            <DsButton key="cancel" type="button" variant="secondary" onClick={() => setDeleteOpen(false)} disabled={busy}>キャンセル</DsButton>
            <DsButton key="delete" type="button" variant="danger" onClick={deleteSelected} loading={busy}>
              {selectedCount}枚を削除
            </DsButton>
          </>
        )}
      >
        <p>削除した画像はイベントアルバムから閲覧・保存できなくなります。</p>
      </DsModal>

      <DsModal
        open={uploadOpen}
        onClose={() => {
          if (busy) return;
          setUploadOpen(false);
          resetUploadDraft();
        }}
        title="イベントアルバムへ追加"
        size="lg"
        dismissible={!busy}
        footer={(
          <>
            <DsButton
              type="button"
              variant="secondary"
              onClick={() => {
                if (uploadStep === 'watermark') {
                  setUploadOpen(false);
                  resetUploadDraft();
                } else if (uploadStep === 'select') {
                  setUploadStep('watermark');
                } else {
                  setUploadStep('select');
                }
              }}
              disabled={busy}
            >
              {uploadStep === 'watermark' ? '閉じる' : '戻る'}
            </DsButton>
            {uploadStep === 'watermark' ? (
              <DsButton type="button" onClick={() => setUploadStep(pendingFiles.length ? 'confirm' : 'select')}>
                {pendingFiles.length ? '設定して確認へ' : '次へ'}
              </DsButton>
            ) : null}
            {uploadStep === 'select' ? (
              <DsButton type="button" onClick={() => fileInputRef.current?.click()}>画像を選択</DsButton>
            ) : null}
            {uploadStep === 'confirm' ? (
              <DsButton
                type="button"
                onClick={uploadPendingFiles}
                loading={busy}
                disabled={previewBusy || !pendingFiles.length}
              >
                {uploadProgress?.error ? '残りを再試行' : 'イベントアルバムへ追加'}
              </DsButton>
            ) : null}
          </>
        )}
      >
        <div className="eig-upload-wizard">
          <div className="eig-stepper" aria-label="イベントアルバム追加の進行状況">
            {UPLOAD_STEPS.map((step, index) => (
              <button
                key={step.id}
                type="button"
                className={`eig-step${uploadStep === step.id ? ' is-current' : ''}${index < uploadStepIndex ? ' is-done' : ''}`}
                disabled={busy || Math.abs(index - uploadStepIndex) !== 1 || (step.id === 'confirm' && !pendingFiles.length)}
                aria-current={uploadStep === step.id ? 'step' : undefined}
                onClick={() => handleStepClick(step.id, index)}
              >
                <span>{step.label}</span>
              </button>
            ))}
          </div>

          {uploadStep === 'watermark' ? (
            <div className="eig-watermark-layout">
              <div className="eig-settings">
                <div className="eig-layer-toggle-group" role="group" aria-label="加工する要素">
                  <button
                    type="button"
                    className={`eig-layer-toggle${settings.logoEnabled ? ' is-active' : ''}`}
                    aria-pressed={settings.logoEnabled}
                    onClick={() => setSettings((current) => ({ ...current, logoEnabled: !current.logoEnabled }))}
                  >
                    <i className="fa-solid fa-image" aria-hidden="true" />
                    <span>透かし</span>
                  </button>
                  <button
                    type="button"
                    className={`eig-layer-toggle${settings.textEnabled ? ' is-active' : ''}`}
                    aria-pressed={settings.textEnabled}
                    onClick={() => setSettings((current) => ({ ...current, textEnabled: !current.textEnabled }))}
                  >
                    <i className="fa-solid fa-font" aria-hidden="true" />
                    <span>テキスト</span>
                  </button>
                </div>

                {!settings.logoEnabled && !settings.textEnabled ? (
                  <div className="eig-settings-note">加工なしで元画像を追加します。</div>
                ) : null}

                {enabledSettingsTabs.length > 1 ? (
                  <div className="eig-settings-tabs" role="tablist" aria-label="設定ツール">
                    {enabledSettingsTabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={visibleSettingsTab === tab.id}
                        className={visibleSettingsTab === tab.id ? 'is-active' : ''}
                        onClick={() => setActiveSettingsTab(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                ) : null}

                {settings.logoEnabled && visibleSettingsTab === 'logo' ? (
                  <section className="eig-settings-section" aria-label="透かし設定">
                    <div className="eig-settings-section__title">透かし設定</div>
                    <label>
                      <span>透かし</span>
                      <DsSelect value={settings.logoPreset} onChange={(event) => setSettings((current) => ({ ...current, logoPreset: event.target.value }))}>
                        {WATERMARK_PRESETS.map((preset) => <option key={preset.id} value={preset.id}>{preset.label}</option>)}
                      </DsSelect>
                    </label>
                    {settings.logoPreset === 'custom' ? (
                      <DsButton type="button" variant="secondary" size="sm" onClick={() => customLogoInputRef.current?.click()}>透過画像を選択</DsButton>
                    ) : null}
                    {logoSupportsFillColor(settings) ? (
                      <div className="eig-logo-color">
                        <div className="eig-settings-section__title">塗りつぶしカラー</div>
                        <div className="eig-color-palette" role="radiogroup" aria-label="透かし塗りつぶしカラー">
                          {LOGO_FILL_COLORS.map((color) => {
                            const isOriginalColor = color.id === 'original';
                            const isActiveColor = settings.logoFillColor === color.id;
                            return (
                              <button
                                key={color.id}
                                type="button"
                                className={`eig-color-swatch eig-color-swatch--${color.id}${isActiveColor ? ' is-active' : ''}`}
                                role="radio"
                                aria-label={color.label}
                                aria-checked={isActiveColor}
                                onClick={() => setSettings((current) => ({ ...current, logoFillColor: color.id }))}
                              >
                                <span
                                  style={isOriginalColor ? undefined : { backgroundColor: getLogoFillColorValue(color, settings) }}
                                >
                                  {isOriginalColor ? <i className="fa-solid fa-rotate-left" aria-hidden="true" /> : null}
                                  {color.id === 'custom' && !isActiveColor ? <i className="fa-solid fa-plus" aria-hidden="true" /> : null}
                                  {isActiveColor ? <b aria-hidden="true"><i className="fa-solid fa-check" /></b> : null}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        {settings.logoFillColor === 'custom' ? (
                          <input className="eig-color-picker" aria-label="自由選択カラー" type="color" value={settings.customLogoFillColor || getLogoFillColorValue('custom', settings)} onChange={(event) => setSettings((current) => ({ ...current, customLogoFillColor: event.target.value }))} />
                        ) : null}
                      </div>
                    ) : null}
                    <label>
                      <span>透かし位置</span>
                      <DsSelect value={settings.logoPosition} onChange={(event) => setSettings((current) => ({ ...current, logoPosition: event.target.value }))}>
                        {WATERMARK_POSITIONS.map((position) => <option key={position.id} value={position.id}>{position.label}</option>)}
                      </DsSelect>
                    </label>
                    <label>
                      <span>透け具合</span>
                      <input type="range" min="0" max="1" step="0.01" value={settings.opacity} onChange={(event) => setSettings((current) => ({ ...current, opacity: Number(event.target.value) }))} />
                      <strong className="eig-range-value">{Math.round(Number(settings.opacity || 0) * 100)}%</strong>
                    </label>
                    <label>
                      <span>ロゴサイズ</span>
                      <input type="range" min="0.08" max="0.32" step="0.01" value={settings.logoSize} onChange={(event) => setSettings((current) => ({ ...current, logoSize: Number(event.target.value) }))} />
                    </label>
                  </section>
                ) : null}

                {settings.textEnabled && visibleSettingsTab === 'text' ? (
                  <section className="eig-settings-section" aria-label="テキスト設定">
                    <div className="eig-settings-section__title">テキスト設定</div>
                    <label>
                      <span>文字</span>
                      <DsSelect value={settings.textPreset} onChange={(event) => setSettings((current) => ({ ...current, textPreset: event.target.value }))}>
                        {TEXT_PRESETS.map((preset) => <option key={preset.id} value={preset.id}>{preset.label}</option>)}
                      </DsSelect>
                    </label>
                    {settings.textPreset === 'custom' ? (
                      <label>
                        <span>任意文字</span>
                        <input value={settings.customText} onChange={(event) => setSettings((current) => ({ ...current, customText: event.target.value }))} />
                      </label>
                    ) : null}
                    <label>
                      <span>文字色</span>
                      <DsSelect value={settings.textColor} onChange={(event) => setSettings((current) => ({ ...current, textColor: event.target.value }))}>
                        <option value="white">白</option>
                        <option value="black">黒</option>
                      </DsSelect>
                    </label>
                    <label>
                      <span>文字位置</span>
                      <DsSelect value={settings.textPosition} onChange={(event) => setSettings((current) => ({ ...current, textPosition: event.target.value }))}>
                        {WATERMARK_POSITIONS.map((position) => <option key={position.id} value={position.id}>{position.label}</option>)}
                      </DsSelect>
                    </label>
                    <label>
                      <span>文字サイズ</span>
                      <input type="range" min="0.02" max="0.07" step="0.005" value={settings.textSize} onChange={(event) => setSettings((current) => ({ ...current, textSize: Number(event.target.value) }))} />
                    </label>
                  </section>
                ) : null}
              </div>
              <div className="eig-preview-panel">
                <div className="eig-preview-controls">
                  <div className="eig-segment" role="group" aria-label="プレビュー比率">
                    <button type="button" className={sampleOrientation === 'landscape' ? 'is-active' : ''} onClick={() => setSampleOrientation('landscape')}>横</button>
                    <button type="button" className={sampleOrientation === 'portrait' ? 'is-active' : ''} onClick={() => setSampleOrientation('portrait')}>縦</button>
                  </div>
                  <div className="eig-segment" role="group" aria-label="プレビュー背景">
                    <button type="button" className={sampleTone === 'dark' ? 'is-active' : ''} onClick={() => setSampleTone('dark')}>暗</button>
                    <button type="button" className={sampleTone === 'light' ? 'is-active' : ''} onClick={() => setSampleTone('light')}>明</button>
                  </div>
                </div>
                <WatermarkSamplePreview
                  settings={settings}
                  eventName={eventName}
                  eventDateLabel={eventDateLabel}
                  orientation={sampleOrientation}
                  tone={sampleTone}
                />
              </div>
            </div>
          ) : null}

          {uploadStep === 'select' ? (
            <div
              className="eig-drop"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => {
                if (!hasExternalFiles(event.dataTransfer)) return;
                event.preventDefault();
              }}
              onDrop={(event) => {
                if (!event.dataTransfer?.files?.length) return;
                event.preventDefault();
                handleSelectedFiles(event.dataTransfer.files);
              }}
            >
              <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true" />
              <strong>画像をここへドラッグ、または選択</strong>
              <span>JPEG / PNG / WebP、1回{MAX_BATCH_FILES}枚まで。20MB超はブラウザ内でリサイズします。</span>
            </div>
          ) : null}

          {uploadStep === 'confirm' ? (
            <div className="eig-confirm">
              <div className="eig-confirm-head">
                <div>
                  <strong>{pendingFiles.length}枚を追加します</strong>
                  <span>先頭1枚の加工後イメージを比率維持で全体表示します。</span>
                </div>
                <DsButton type="button" variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                  選び直す
                </DsButton>
              </div>
              {previewBusy ? (
                <div className="eig-empty">加工後プレビューを生成しています。</div>
              ) : (
                <div className="eig-preview-grid">
                  {processedPreviews.map((preview) => (
                    <figure key={preview.url} className="eig-processed-preview">
                      <img src={preview.url} alt={`${preview.name} の加工後プレビュー`} />
                      <figcaption>
                        <span>{preview.name}</span>
                        <small>{formatBytes(preview.originalSize)} → {formatBytes(preview.fileSize)}{preview.resized ? ' / リサイズ済み' : ''}</small>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
              {uploadProgress ? (
                <div className={`eig-upload-progress${uploadProgress.error ? ' is-error' : ''}`} aria-live="polite">
                  <div className="eig-upload-progress__head">
                    <strong>
                      {uploadProgress.error
                        ? 'アップロードが途中で止まりました'
                        : uploadProgress.phase === 'registered'
                          ? 'アルバムへ登録しています'
                          : uploadProgress.phase === 'registering'
                            ? 'アップロード済み画像を登録しています'
                            : uploadProgress.phase === 'processing'
                              ? '画像を準備しています'
                              : '画像をアップロードしています'}
                    </strong>
                    <span>{Math.min(uploadProgress.processed || 0, uploadProgress.total || 0)} / {uploadProgress.total || pendingFiles.length}枚</span>
                  </div>
                  <progress max={uploadProgress.total || pendingFiles.length || 1} value={Math.min(uploadProgress.processed || 0, uploadProgress.total || pendingFiles.length || 1)} />
                  <span>
                    {uploadProgress.error
                      ? `${uploadProgress.registered || 0}枚は登録済みです。再試行すると残りから続けます。`
                      : uploadProgress.currentName || `${uploadProgress.registered || 0}枚を登録済み`}
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </DsModal>

      <DsModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        title="イベントアルバム"
        size="lg"
        bodyClassName="eig-viewer-modal"
        footer={(
          <>
            <DsButton type="button" variant="secondary" onClick={() => setGalleryOpen(false)}>閉じる</DsButton>
            {canDownload && selectedCount > 0 ? (
              <DsButton type="button" className="eig-download-action" onClick={downloadSelected}>
                <i className="fa-solid fa-download" aria-hidden="true" />
                ダウンロード開始
                <span className="eig-download-badge">{selectedCount}</span>
              </DsButton>
            ) : null}
          </>
        )}
      >
        {activeImage ? (
          <div className="eig-viewer">
            <div className="eig-modal-toolbar">
              <span className="eig-viewer-count">{activeImageIndex + 1} / {previewImages.length}</span>
              <DsButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (selectedAllInView) setSelectedIds(new Set());
                  else setSelectedIds(new Set(previewImages.map((image) => image.id)));
                }}
              >
                {selectedAllInView ? '全解除' : 'すべて選択'}
              </DsButton>
            </div>
            <div className="eig-viewer-stage">
              {previewImages.length > 1 ? (
                <button type="button" className="eig-viewer-nav eig-viewer-nav--prev" onClick={() => moveActiveImage(-1)} aria-label="前の画像">
                  <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                </button>
              ) : null}
              <img src={buildEventImageFileUrl(eventUuid, activeImage.id)} alt={activeImage.file_name || 'イベントアルバム画像'} />
              <button
                type="button"
                className={`eig-viewer-check${selectedIds.has(activeImage.id) ? ' is-selected' : ''}`}
                aria-label={selectedIds.has(activeImage.id) ? '選択を解除' : 'この画像を選択'}
                aria-pressed={selectedIds.has(activeImage.id)}
                onClick={() => toggleSelected(activeImage.id)}
              >
                <i className="fa-solid fa-check" aria-hidden="true" />
              </button>
              {previewImages.length > 1 ? (
                <button type="button" className="eig-viewer-nav eig-viewer-nav--next" onClick={() => moveActiveImage(1)} aria-label="次の画像">
                  <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <div className="eig-viewer-meta">
              <strong>{activeImage.file_name || 'イベントアルバム画像'}</strong>
              <span>{formatBytes(activeImage.file_size)} / {activeImage.created_at ? formatDateTimeJST(activeImage.created_at) : ''}</span>
            </div>
            {previewImages.length > 1 ? (
              <div className="eig-viewer-strip" aria-label="イベントアルバムサムネイル">
                {previewImages.map((image) => (
                  <button
                    key={image.id}
                    type="button"
                    className={`eig-viewer-strip-item${activeImage.id === image.id ? ' is-active' : ''}${selectedIds.has(image.id) ? ' is-selected' : ''}`}
                    onClick={() => setActiveImageId(image.id)}
                    aria-label={`${image.file_name || 'イベントアルバム画像'}を表示`}
                  >
                    <img src={buildEventImageFileUrl(eventUuid, image.id)} alt="" loading="lazy" />
                    {selectedIds.has(image.id) ? <span><i className="fa-solid fa-check" aria-hidden="true" /></span> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="eig-empty">表示できるイベントアルバム画像がありません。</div>
        )}
      </DsModal>
    </div>
  );
}
